import strings from "global/constants/StringConstants";
import { signerFieldValidationPatterns } from "global/constants/ConstantObject";
import { focusRing, neutral } from "theme/colors";
import { darkenToContrast } from "utils/accessibility";
import SignIcon from "../../assets/icons/FieldIcons/SignIcon.svg";
import InitialIcon from "../../assets/icons/FieldIcons/InitialIcon.svg";
import TextIcon from "../../assets/icons/FieldIcons/TextIcon.svg";
import CheckboxIcon from "../../assets/icons/FieldIcons/Checkbox.svg";
import RadioButtonIcon from "../../assets/icons/FieldIcons/RadioButton.svg";
import DateIcon from "../../assets/icons/FieldIcons/DateIcon.svg";
import NameIcon from "../../assets/icons/FieldIcons/NameIcon.svg";
import EmailIcon from "../../assets/icons/FieldIcons/EmailIcon.svg";
import CompanyNameIcon from "../../assets/icons/FieldIcons/CompanyNameIcon.svg";
import TitleIcon from "../../assets/icons/FieldIcons/TitleIcon.svg";
import DateFieldIcon from "../../assets/icons/FieldIcons/DateFieldIcon.svg";
import AttachmentIcon from "../../assets/icons/FieldIcons/AttachmentIcon.svg";
import AttachmentFieldIcon from "../../assets/icons/FieldIcons/AttachmentFieldIcon.svg";
import EmailFieldIcon from "../../assets/icons/FieldIcons/EmailFieldIcon.svg";
import InitialsFieldIcon from "../../assets/icons/FieldIcons/InitialsFieldIcon.svg";
import NameFieldIcon from "../../assets/icons/FieldIcons/NameFieldIcon.svg";
import SignatureFieldIcon from "../../assets/icons/FieldIcons/SignatureFieldIcon.svg";
import TextFieldIcon from "../../assets/icons/FieldIcons/TextFieldIcon.svg";
import CompanyNameFieldIcon from "../../assets/icons/FieldIcons/CompanyNameFieldIcon.svg";
import TitleFieldIcon from "../../assets/icons/FieldIcons/TitleFieldIcon.svg";
import { decodeAttachmentValue } from "./ParticipantScreen/components/AttachmentPolicy";
import {
  getLoadedAccountAttachmentConfig,
  loadAccountAttachmentConfig,
} from "helpers/attachmentConfig";
import { PaperclipIcon } from "./ParticipantScreen/components/SignerExperienceIcons";
import signerAttachmentTokens from "./ParticipantScreen/SignerAttachments.tokens";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Stack,
  Theme,
  Typography,
} from "@mui/material";
import { SignRequestStyles } from "./SignRequest.styles";
import editorTokens from "./SignRequestEditor.tokens";
import { ChevronDownIcon } from "./components/EditorIcons";
import {
  detectFields,
  fetchDocumentDimensions,
  getContactexclude,
  getEnvelopeId,
  saveDraftSignRequest,
  saveTemplate,
  sendPdfSignRequest,
  updateSignRequest,
} from "./SignRequestService";
import {
  capitalizeText,
  convertEstToLocalTimezone,
  isTruthy,
  openSuccessNotification,
  sanitizeSignerEmail,
} from "helpers/methods";
import { uploadUnsignedPdf } from "./SignRequestService";
import { useAppSelector } from "redux/hooks";
import { CustomField } from "models/interfaces";
import {
  Coordinates,
  FileField,
  RequestFile,
  RequestParticipant,
  Reviewers,
  SignRequest,
  SignRequestErrors,
} from "models/interfaces";
import {
  handleErrorMessage,
  isPhoneValid,
  openErrorNotification,
} from "helpers/methods";
import { initialSelectedFile } from "Context";
import { hasAccessTo } from "layouts/Config";
import React from "react";
import { getColor } from "screens/MySignRequests/MySignRequestHelper";
import {
  addFileInSignRequest,
  removeFileFromSignRequest,
  setAssignExistingFields,
  setContacts,
  setErrors,
  setGoBackPopup,
  setLoading,
  setReduxPdfApprovals,
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  setHasAutoDetected,
  setPendingAiFields,
  setReduxSignRequest,
  setSelectedField,
  setSelectedFieldDrawer,
  setUploadedFileNames,
  setUploadedFileNamesDelete,
  updateFileInSignRequest,
  updateFileWithFields,
} from "redux/appSlice";
import CheckBoxField from "./components/Fields/CheckBoxField";
import RadioField from "./components/Fields/RadioField";
import DateField from "./components/Fields/DateField";
import ReadOnlyFields from "./components/Fields/ReadOnlyFields";
import { CustomInput } from "global/components";
import { AppModal } from "global/components/AppModal";
import {
  getParticipantEmailError,
  getParticipantNameError,
  isValidParticipantNameLength,
  setCoordinates,
} from "screens/Shared/CommonHelper";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { updateTemplate } from "screens/Templates/MyTemplatesService";
import OrangeDateFieldIcon from "assets/icons/OrangeDateFieldIcon.svg";
import OrangeEmailFieldIcon from "assets/icons/OrangeEmailFieldIcon.svg";
import OrangeInitialsFieldIcon from "assets/icons/OrangeInitialsFieldIcon.svg";
import OrangeNameFieldIcon from "assets/icons/OrangeNameFieldIcon.svg";
import OrangeSignatureFieldIcon from "assets/icons/OrangeSignatureFieldIcon.svg";
import OrangeTextFieldIcon from "assets/icons/OrangeTextFieldIcon.svg";
import moment from "moment";
import { nanoid } from "nanoid";

export const MIN_HEIGHT_PX = 24;
export const MIN_WIDTH_PX = 56;
export const DEFAULT_HEIGHT_PX = MIN_HEIGHT_PX * 2.5;
export const DEFAULT_WIDTH_PX = MIN_WIDTH_PX * 1.3;
export const MOBILE_MIN_HEIGHT_PX = MIN_HEIGHT_PX / 1.2; // 12
export const MOBILE_MIN_WIDTH_PX = MIN_WIDTH_PX / 1.2; // 28
export const MOBILE_DEFAULT_WIDTH_PX = DEFAULT_WIDTH_PX;
export const DATE_FIELD_WIDTH_PX = MIN_WIDTH_PX * 1.75;
export const MOBILE_DATE_FIELD_WIDTH_PX = MIN_WIDTH_PX / 1.2;
export const PDF_VIEWER_PAGE_SELECTOR = ".react-pdf__Page";

// Same design palette as utils/SignerColors, darkened the same way for WCAG
// 1.4.3/1.4.11 (see darkenToContrast). The names describe the design hue.
export const fieldColor = [
  "#FF6B6B", // Red Coral
  "#FFB347", // Orange
  "#FFD700", // Gold
  "#00C49A", // Teal
  "#0088FE", // Blue
  "#A28EFF", // Lavender
  "#FF69B4", // Hot Pink
  "#FF7F50", // Coral
  "#40E0D0", // Turquoise
  "#6495ED", // Cornflower Blue
  "#DC143C", // Crimson
  "#2E8B57", // Sea Green
  "#4B0082", // Indigo (was Light Sea Green, which darkened onto Turquoise)
  "#9932CC", // Dark Orchid
  "#880E4F", // Wine (was Dark Orange, which darkened onto Orange)
  "#8D6E63", // Taupe (was Medium Sea Green, which darkened onto Sea Green)
  "#B22222", // Firebrick
  "#00CED1", // Dark Turquoise
  "#9370DB", // Medium Purple
  "#3E2723", // Espresso (was Dodger Blue, which darkened onto Blue)
].map((c) => darkenToContrast(c));

/**
 * Custom fields. A field with `signerIndex === ANY_SIGNER` belongs to whichever
 * signer fills it first; the server records that signer in `field.filledBy`.
 * Mirrors PdfField.ANY_SIGNER / isFillableBy on the backend.
 */
export const ANY_SIGNER = -1;
/** Smallest size, in percent of the page, a field can be resized to by keyboard. */
export const MIN_FIELD_SIZE_PERCENT = 2;

/**
 * New size, in percent of the page, of a placed field after one Alt+Arrow
 * press (keyboard resize, WCAG 2.1.1). Right/Down grow, Left/Up shrink by
 * `step`. The field never shrinks below MIN_FIELD_SIZE_PERCENT and never grows
 * past the page's right or bottom edge. A checkbox stays square, as under a
 * mouse resize: any arrow changes its one size, and that size is clamped by
 * both edges together so forcing width = height can't push it off the page.
 */
export const resizeFieldByKey = (
  field: {
    fieldPageX?: number;
    fieldPageY?: number;
    fieldPageWidth?: number;
    fieldPageHeight?: number;
  },
  key: string,
  step: number,
  isSquare: boolean,
): { fieldPageWidth: number; fieldPageHeight: number } => {
  const num = (v: unknown, fallback: number) =>
    typeof v === "number" ? v : fallback;
  const x = num(field.fieldPageX, 0);
  const y = num(field.fieldPageY, 0);
  const grow = key === "ArrowRight" || key === "ArrowDown";
  const clamp = (v: number, max: number) =>
    Math.min(Math.max(v, MIN_FIELD_SIZE_PERCENT), max);

  if (isSquare) {
    const current = num(field.fieldPageHeight, MIN_FIELD_SIZE_PERCENT);
    const size = clamp(current + (grow ? step : -step), Math.min(100 - x, 100 - y));
    return { fieldPageWidth: size, fieldPageHeight: size };
  }
  let w = num(field.fieldPageWidth, MIN_FIELD_SIZE_PERCENT);
  let h = num(field.fieldPageHeight, MIN_FIELD_SIZE_PERCENT);
  if (key === "ArrowRight") w += step;
  else if (key === "ArrowLeft") w -= step;
  else if (key === "ArrowDown") h += step;
  else if (key === "ArrowUp") h -= step;
  return { fieldPageWidth: clamp(w, 100 - x), fieldPageHeight: clamp(h, 100 - y) };
};

/** id of the hidden keyboard help (rendered by DocumentViewer) that every
 *  placed field points to with aria-describedby. */
export const PLACED_FIELD_HELP_ID = "placed-field-keyboard-help";
/** Any-signer fields are drawn in a neutral tone so no signer colour claims them. */
export const ANY_SIGNER_COLOR = neutral[500];
/** Data-only field types that may be assigned to "any signer" (never signature-style). */
export const ANY_SIGNER_FIELD_TYPES: string[] = [
  strings.text,
  strings.date,
  strings.dropDown,
  strings.checkbox,
  strings.radioButton,
];
/** Text-like custom fields that take the length / pattern validations. */
export const PATTERN_FIELD_TYPES: string[] = [
  strings.text,
  strings.dropDown,
];

export const fieldColorFor = (signerIndex: number | undefined): string =>
  signerIndex === ANY_SIGNER
    ? ANY_SIGNER_COLOR
    : fieldColor[signerIndex as number];

export const isAnySignerField = (field: { signerIndex?: number }): boolean =>
  field?.signerIndex === ANY_SIGNER;

/**
 * Whether the signer identified by (id, email) may fill the field: their own
 * field, or an any-signer field nobody else has filled yet.
 */
export const isFieldFillableBy = (
  field: { signerIndex?: number; filledBy?: string },
  signerId: number | undefined,
  signerEmail?: string,
): boolean => {
  if (!isAnySignerField(field)) {
    return signerId !== undefined && signerId >= 0 && field?.signerIndex === signerId;
  }
  const filledBy = (field.filledBy ?? "").trim();
  return (
    filledBy === "" ||
    filledBy.toLowerCase() === (signerEmail ?? "").trim().toLowerCase()
  );
};

/** Own field, or an any-signer field (shown to everyone, editable while unclaimed). */
export const isFieldVisibleTo = (
  field: { signerIndex?: number },
  signerId: number | undefined,
): boolean =>
  isAnySignerField(field) ||
  (signerId !== undefined && signerId >= 0 && field?.signerIndex === signerId);

/**
 * Custom field library. A saved definition is dragged from the "Custom Fields"
 * panel exactly like a palette type; the drag carries the key
 * `custom:<id>`, which the drop handlers resolve back to the definition.
 */
export const CUSTOM_FIELD_KEY_PREFIX = "custom:";
/** Types a library definition may have (data fields only, like any-signer). */
export const LIBRARY_FIELD_TYPES: string[] = [
  strings.text,
  strings.date,
  strings.dropDown,
  strings.checkbox,
  strings.radioButton,
];

export const customFieldKey = (id: string) => CUSTOM_FIELD_KEY_PREFIX + id;

export const isCustomFieldKey = (key: string | null | undefined): boolean =>
  !!key && key.startsWith(CUSTOM_FIELD_KEY_PREFIX);

export const findCustomFieldByKey = (
  key: string | null | undefined,
  customFields: CustomField[],
): CustomField | undefined =>
  isCustomFieldKey(key)
    ? customFields.find(
      (field) => field.id === (key as string).slice(CUSTOM_FIELD_KEY_PREFIX.length),
    )
    : undefined;

/** The palette type behind a drag key: the definition's type, or the key itself. */
export const paletteTypeOf = (
  key: string | null | undefined,
  customFields: CustomField[],
): string =>
  findCustomFieldByKey(key, customFields)?.fieldType ?? (key ?? "");

/** Placement-specific keys a library config must never carry onto a document. */
const PLACEMENT_KEYS = [
  "id",
  "name",
  "type",
  "value",
  // The label is generated per placement -- "Kenil-text-1", "Kenil-text-2" -- exactly as it is
  // for a field dragged from the Fields panel. Letting a definition supply it gave every
  // placement of that definition the same label, which collides in Web Forms (the label is the
  // form key), in the CSV export and in the audit trail.
  "label",
  "filledBy",
  "existingField",
  "hidden",
  "groupId",
  "page",
  "x",
  "y",
  "w",
  "h",
  "frameX",
  "frameY",
  "fieldPageX",
  "fieldPageY",
  "fieldPageWidth",
  "fieldPageHeight",
  "width",
  "height",
  "top",
  "left",
  "color",
  "signerIndex",
  "autoFilled",
];

/**
 * What a field placed from a library definition starts with: the saved config
 * (validation, options, default ...) minus anything that belongs to the
 * placement, plus the link back to the definition. Spread AFTER the builder's
 * own defaults so the definition wins -- except for the PLACEMENT_KEYS, which
 * the drop handler owns. The label is one of those: a placed custom field is
 * named "Kenil-text-1" like any other field.
 */
export const libraryFieldProps = (
  definition: CustomField,
  isSelfSign: boolean = false,
) => {
  const props: Record<string, any> = {};
  Object.entries(definition.config ?? {}).forEach(([key, value]) => {
    if (!PLACEMENT_KEYS.includes(key) && value !== undefined) {
      props[key] = value;
    }
  });
  // No label fallback on purpose: "label" is a PLACEMENT_KEY, so the label the drop handler
  // generated is the one that stays. Putting the definition's name back here would undo that.
  if (definition.fieldType === strings.dropDown && !props.listItems?.length) {
    props.listItems = customFieldDefaults(strings.dropDown).listItems;
  }
  // In "I'm the only signer" the sender IS the signer, so a default has to be a real value from the
  // moment the field is placed -- the one deliberate exception to `value` being a PLACEMENT_KEY.
  // Everywhere else the server copies the default into `value` at send (applyDefaultValues) and the
  // signer receives it; in self-sign there is no later hand-off, so the field showed nothing, could
  // not be told apart from an empty one, and a read-only field looked unfilled while still passing.
  if (isSelfSign && typeof props.defaultValue === "string" && props.defaultValue.trim()) {
    props.value = props.defaultValue;
  }
  props.customFieldId = definition.id;
  return props;
};

/**
 * The largest Min/Max Length a sender may set on ANY text field — library or Fields-panel.
 * Mirrors DocumentSignHelper.MAX_FIELD_LENGTH (the server only enforces it on library fields).
 */
export const MAX_FIELD_LENGTH = 4000;

/** Message for a length past MAX_FIELD_LENGTH — short enough for a half-width input. */
export const FIELD_LENGTH_LIMIT_MESSAGE = `Must be ${MAX_FIELD_LENGTH} or less.`;

/** Longest a length input may be typed — enough for MAX_FIELD_LENGTH plus a digit to refuse. */
const LENGTH_INPUT_DIGITS = 5;

/**
 * A Min/Max Length input's raw text reduced to what the input accepts: digits only, at most
 * LENGTH_INPUT_DIGITS of them, so an endless run of digits (or a pasted "1e9") can't get in.
 * Returns "" for an emptied input so callers can tell "cleared" from "0".
 */
export const toLengthDigits = (raw: unknown): string =>
  String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, LENGTH_INPUT_DIGITS);

/** Keys a number input accepts that are never part of a whole, non-negative length. */
const NON_LENGTH_KEYS = ["e", "E", "+", "-", ".", ","];

/** `onKeyDown` for Min/Max Length inputs: refuses the non-digit keys a number input allows. */
export const blockNonLengthKeys = (event: React.KeyboardEvent) => {
  if (NON_LENGTH_KEYS.includes(event.key)) event.preventDefault();
};

/**
 * Why a placed custom field could not be sent, or "" when it is fine. Mirrors the server's
 * DocumentSignHelper.validateCustomFieldConfiguration so the sender is stopped in the editor, beside
 * the field they can fix, instead of by a toast two steps later. Library fields only -- a field
 * dragged from the Fields panel is sent exactly as it always was.
 */
export const customFieldConfigError = (field: FileField): string => {
  if (!(field as any).customFieldId) return "";
  const label = field.label || "this field";
  const isText = field.type === strings.text;
  const isDropDown = field.type === strings.dropDown;
  const min = field.minLength ?? 0;
  const max = field.maxLength ?? 0;

  if (field.customValidation) {
    const pattern = (field.validationPattern || "").trim();
    if (!pattern) return `Enter a validation pattern for ${label}.`;
    try {
      new RegExp(pattern);
    } catch {
      return `The validation pattern of ${label} is not valid.`;
    }
  }
  if (isText) {
    if (min < 0 || max < 0) return `The length limits of ${label} cannot be negative.`;
    if (min > MAX_FIELD_LENGTH || max > MAX_FIELD_LENGTH)
      return `The length limits of ${label} cannot exceed ${MAX_FIELD_LENGTH}.`;
    if (max > 0 && min > max)
      return `The minimum length of ${label} exceeds its maximum length.`;
  }
  if (isDropDown) {
    // Checked BEFORE the blanks are filtered out. The Custom Field modal already refuses a blank
    // option ("Give every option a label."), but the Properties panel lets a placed field's option
    // be cleared, and a blank row reaches the signer as a gap in the list that cannot be picked.
    const options = (field.listItems ?? []).map((item) => (item?.text ?? "").trim());
    if (!options.length || options.every((text) => !text))
      return `${label} needs at least one option.`;
    if (options.some((text) => !text))
      return `Give every option of ${label} a label, or remove the blank one.`;
    if (new Set(options).size !== options.length)
      return `${label} has duplicate options.`;
  }

  // String(), not a bare .trim(): a ticked checkbox holds the BOOLEAN `true` (CheckBoxField and the
  // signer's CustomCheckBoxFieldsElement both dispatch `!isChecked`), so calling .trim() on it threw a
  // TypeError here. The throw was swallowed by handleErrorMessage -- which stays silent for an error
  // with no HTTP status -- so Next simply did nothing, with no toast to explain it. Normal fields were
  // unaffected only because this function returns early for them.
  const filled =
    String(field.defaultValue ?? "").trim() || String(field.value ?? "").trim();
  if (field.required && field.readOnly && !filled) {
    return `${label} is required but read only, so the signer can never fill it.`;
  }

  const defaultValue = (field.defaultValue ?? "").trim();
  if (defaultValue) {
    if (isDropDown) {
      const options = (field.listItems ?? []).map((item) => (item?.text ?? "").trim());
      if (!options.includes(defaultValue))
        return `The default value of ${label} is not one of its options.`;
    }
    if (isText) {
      if (max > 0 && defaultValue.length > max)
        return `The default value of ${label} is longer than its maximum length.`;
      if (min > 0 && defaultValue.length < min)
        return `The default value of ${label} is shorter than its minimum length.`;
      if (field.validationPattern && field.validationPattern !== "none") {
        let pattern: RegExp | null = null;
        try {
          pattern = field.customValidation
            ? new RegExp(field.validationPattern)
            : (signerFieldValidationPatterns[
              field.validationPattern as keyof typeof signerFieldValidationPatterns
            ] ?? null);
        } catch {
          pattern = null;
        }
        if (pattern && !pattern.test(defaultValue))
          return (
            field.validationMessage ||
            `The default value of ${label} does not match its validation rule.`
          );
      }
    }
  }
  return "";
};

/** Extra properties a freshly placed custom field starts with (see FileField). */
export const customFieldDefaults = (type: string) => ({
  customValidation: false,
  minLength: 0,
  defaultValue: "",
  listItems:
    type === strings.dropDown ? [{ id: "option_1", text: "Option 1" }] : [],
});

export const Fields = (theme: Theme) => {
  const classes = SignRequestStyles(theme);
  return {
    Fields: [
      {
        type: strings.signature,
        icon: SignIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={SignatureFieldIcon}
            sx={classes.responsiveIcon}
            alt="Signature field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.initials,
        icon: InitialIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={InitialsFieldIcon}
            sx={classes.responsiveIcon}
            alt="Initials field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.text,
        icon: TextIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={TextFieldIcon}
            sx={classes.responsiveIcon}
            alt="Text field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.dropDown,
        icon: TextIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={TextFieldIcon}
            sx={classes.responsiveIcon}
            alt="Dropdown field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.checkbox,
        icon: CheckboxIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={CheckboxIcon}
            sx={classes.responsiveIcon}
            alt="Checkbox field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.radioButton,
        icon: RadioButtonIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={RadioButtonIcon}
            sx={classes.responsiveIcon}
            alt="Radio button field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.dateSigned,
        icon: DateIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={DateFieldIcon}
            sx={classes.responsiveIcon}
            alt="Date field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.date,
        icon: DateIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={DateFieldIcon}
            sx={classes.responsiveIcon}
            alt="Date field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.attachment,
        icon: AttachmentIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={AttachmentFieldIcon}
            sx={classes.responsiveIcon}
            alt="Attachment field icon"
            aria-hidden="true"
          />
        ),
      },
    ],
    "Personal Info": [
      {
        type: strings.name,
        icon: NameIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={NameFieldIcon}
            sx={classes.responsiveIcon}
            alt="Name field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.email,
        icon: EmailIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={EmailFieldIcon}
            sx={classes.responsiveIcon}
            alt="Email field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.companyName,
        icon: CompanyNameIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={CompanyNameFieldIcon}
            sx={classes.responsiveIcon}
            alt="Company name field icon"
            aria-hidden="true"
          />
        ),
      },
      {
        type: strings.title,
        icon: TitleIcon,
        activeIcon: (
          <Box
            component={"img"}
            src={TitleFieldIcon}
            sx={classes.responsiveIcon}
            alt="Title field icon"
            aria-hidden="true"
          />
        ),
      },
    ],
  };
};

export const FieldsProperties = {
  Fields: [
    {
      type: strings.signature,
      icon: SignIcon,
      color: "primary.main",
      ativeIcon: (
        <Box
          component={"img"}
          src={OrangeSignatureFieldIcon}
          alt="Signature field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.initials,
      icon: InitialIcon,
      color: "primary.main",
      ativeIcon: (
        <Box
          component={"img"}
          src={OrangeInitialsFieldIcon}
          alt="Initials field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.text,
      icon: TextIcon,
      ccolor: "primary.main",
      ativeIcon: (
        <Box
          component={"img"}
          src={OrangeTextFieldIcon}
          alt="Text field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.dropDown,
      icon: TextIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={OrangeTextFieldIcon}
          alt="Dropdown field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.checkbox,
      icon: CheckboxIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={CheckboxIcon}
          alt="Checkbox field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.radioButton,
      icon: RadioButtonIcon,
      color: "#211446",
      activeIcon: (
        <Box
          component={"img"}
          src={RadioButtonIcon}
          alt="Radio button field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.radioButton,
      icon: RadioButtonIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={RadioButtonIcon}
          alt="Radio button field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.dateSigned,
      icon: DateIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={OrangeDateFieldIcon}
          alt="Date field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.date,
      icon: DateIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={OrangeDateFieldIcon}
          alt="Date field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.date,
      icon: DateIcon,
      color: "#211446",
      activeIcon: (
        <Box
          component={"img"}
          src={OrangeDateFieldIcon}
          alt="Date field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.attachment,
      icon: AttachmentIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={AttachmentFieldIcon}
          alt="Attachment field icon"
          aria-hidden="true"
        />
      ),
    },
  ],
  "Personal Info": [
    {
      type: strings.name,
      icon: NameIcon,
      color: "primary.main",
      ativeIcon: (
        <Box
          component={"img"}
          src={OrangeNameFieldIcon}
          alt="Name field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.email,
      icon: EmailIcon,
      color: "primary.main",
      ativeIcon: (
        <Box
          component={"img"}
          src={OrangeEmailFieldIcon}
          alt="Email field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.companyName,
      icon: CompanyNameIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={CompanyNameFieldIcon}
          alt="Company name field icon"
          aria-hidden="true"
        />
      ),
    },
    {
      type: strings.title,
      icon: TitleIcon,
      color: "primary.main",
      activeIcon: (
        <Box
          component={"img"}
          src={TitleFieldIcon}
          alt="Title field icon"
          aria-hidden="true"
        />
      ),
    },
  ],
};

// Run AI field detection as soon as a document finishes uploading, so the
// "Auto-place" button is ready by the time the editor is opened (instead of
// triggering the detect call only when the doc is displayed). Fire-and-forget:
// never blocks the upload flow. The editor keeps its own detect effect as a
// fallback — on failure we clear the guard flag so that fallback can retry.
// `unsignedDoc` keys the guard state (the editor reads it by that name);
// `unsignedPdf` is the name the server stored the file under -- a converted
// upload (`offer.docx`) is stored as `offer.pdf`, and only that name exists in S3.
export const triggerAutoDetectFields = async (
  envelopeId: string,
  unsignedDoc: string,
  dispatch: any,
  unsignedPdf?: string,
  isRequest: boolean = true,
) => {
  if (!envelopeId || !unsignedDoc) return;
  // Set the guard up-front so the editor's fallback effect doesn't fire a
  // duplicate detect while this call is still in flight.
  dispatch(setHasAutoDetected({ docId: unsignedDoc, detected: true }));
  try {
    const response = await detectFields(
      envelopeId,
      unsignedPdf || unsignedDoc,
      isRequest,
    );
    const detected: FileField[] = Array.isArray(response) ? response : [];
    if (detected.length > 0) {
      dispatch(setPendingAiFields({ docId: unsignedDoc, fields: detected }));
    }
  } catch (error: any) {
    // Fail silently during upload and clear the guard so the editor's
    // fallback effect can retry when the document is displayed.
    dispatch(setHasAutoDetected({ docId: unsignedDoc, detected: false }));
  }
};

export const uploadFiles = async (
  files: any[],
  dispatch: any,
  isRequest: boolean,
  envelopeId: string,
  // driveUpload: boolean = false,
  abortSignal?: AbortSignal,
) => {
  try {
    const updatedFiles = (
      await Promise.all(
        files.map(async (file: any, index: number) => {
          let formData = new FormData();
          formData.append("file", file, file.name);
          let requestFile: RequestFile = {
            ...initialSelectedFile,
            unsignedDoc: file.name,
            isUploading: true,
          };
          dispatch(addFileInSignRequest(requestFile));
          dispatch(setUploadedFileNames([file.name]));
          try {
            const response = await uploadUnsignedPdf(
              formData,
              isRequest,
              envelopeId,
              abortSignal!,
            );
            requestFile = {
              ...initialSelectedFile,
              isUploading: false,
              unsignedDoc: requestFile.unsignedDoc,
              unsignedDocUrl: response.unsignedPdfUrl,
              /*
                The server's own name for the stored file, which is the only
                name it will answer to. Not the same as `unsignedDoc`: a Word,
                Excel or PowerPoint upload is converted, so `offer.docx` is
                stored as `offer.pdf`. `unsignedDoc` stays the name the sender
                picked, for display.
              */
              unsignedPdf: response.unsignedPdf ?? requestFile.unsignedDoc,
            };
            dispatch(updateFileInSignRequest(requestFile));

            // Kick off AI field detection now (background) so the editor's
            // Auto-place button is ready when the doc is displayed.
            triggerAutoDetectFields(
              envelopeId,
              requestFile.unsignedDoc,
              dispatch,
              requestFile.unsignedPdf,
              isRequest,
            );

            const filteredFields = await response.fields.filter(
              (field: any) =>
                field.type !== "drop_down" && field.type !== "button",
            );

            if (filteredFields.length) {
              requestFile.fields = filteredFields;
              setIdOnEachField([requestFile]);
              dispatch(
                setAssignExistingFields({
                  fileName: requestFile.unsignedDoc,
                  fieldsExist: true,
                }),
              );
              dispatch(updateFileInSignRequest(requestFile));
              dispatch(updateFileWithFields(requestFile));
              return requestFile;
            }
            return null;
          } catch (error: any) {
            // Upload failed for this file: remove the stuck "Uploading..." row
            // and stop its spinner instead of leaving it pending forever.
            dispatch(removeFileFromSignRequest(requestFile));
            dispatch(setUploadedFileNamesDelete(file.name));
            handleErrorMessage(error);
            return null;
          }
        }),
      )
    ).filter((result): result is RequestFile => result !== null);
  } catch (error: any) {
    handleErrorMessage(error);
  }
};

// export const getFieldSize = (selectedField: any) => {
//   switch (selectedField) {
//     case strings.signature:
//     case strings.initials:
//       return [130, 65];
//     case strings.text:
//     case strings.name:
//     case strings.email:
//     case strings.dateSigned:
//     case strings.companyName:
//     case strings.title:
//       return [DEFAULT_WIDTH_PX, 32];
//     case strings.checkbox:
//       return [20, 20];
//     default:
//       return [MIN_WIDTH_PX, MIN_HEIGHT_PX];
//   }
// };

export const getFieldSize = (selectedField: any) => {
  switch (selectedField) {
    case strings.signature:
    case strings.initials:
      return [130, 70];
    case strings.text:
    case strings.dropDown:
    case strings.name:
    case strings.email:
    case strings.dateSigned:
    case strings.companyName:
    case strings.title:
      return [DEFAULT_WIDTH_PX * 2, 32];
    case strings.date:
      return [DATE_FIELD_WIDTH_PX * 2, MIN_HEIGHT_PX * 2 - 8];
    case strings.attachment:
      return [DATE_FIELD_WIDTH_PX * 2, MIN_HEIGHT_PX * 2.25];
    case strings.checkbox:
      return [22, 22];
    case strings.radioButton:
      return [26, 26];
    default:
      return [MIN_WIDTH_PX * 2, MIN_HEIGHT_PX * 2];
  }
};

// export const getFieldSizeMobile = (selectedField: any): [number, number] => {
//   // Scale factor: mobile page width / desktop page width = 261/1143 = 0.228
//   // So mobile field sizes = desktop field sizes * 0.228
//   const scale = 261 / 1143; // ≈ 0.228

//   switch (selectedField) {
//     case strings.signature:
//     case strings.initials:
//       return [130 * scale, 65 * scale]; // [29.6, 14.8]

//     case strings.text:
//     case strings.name:
//     case strings.email:
//     case strings.dateSigned:
//     case strings.companyName:
//     case strings.title:
//       return [DEFAULT_WIDTH_PX * scale, 32 * scale]; // [31.9, 7.3]

//     case strings.checkbox:
//       return [20 * scale, 20 * scale]; // [4.56, 4.56]

//     default:
//       return [MIN_WIDTH_PX * scale, MIN_HEIGHT_PX * scale];
//   }
// };

export const getFieldSizeMobile = (selectedField: any): [number, number] => {
  switch (selectedField) {
    case strings.signature:
    case strings.initials:
      return [146, 72];

    case strings.text:
    case strings.name:
    case strings.email:
    case strings.dateSigned:
    case strings.companyName:
    case strings.title:
      return [MOBILE_DEFAULT_WIDTH_PX * 2, 34];

    case strings.date:
      return [MOBILE_DATE_FIELD_WIDTH_PX, MIN_HEIGHT_PX - 4];

    case strings.attachment:
      return [MOBILE_DATE_FIELD_WIDTH_PX, MIN_HEIGHT_PX];

    case strings.checkbox:
      return [26, 26];

    case strings.radioButton:
      return [26, 26];

    default:
      return [MOBILE_MIN_WIDTH_PX * 2, MOBILE_MIN_HEIGHT_PX * 2];
  }
};

// export const getFieldSizeMobile = (selectedField: any, pageWidth: number): [number, number] => {
//   // Define sizes as % of page width — works on any screen size
//   switch (selectedField) {
//     case strings.signature:
//     case strings.initials:
//       return [
//         pageWidth * 0.17,
//         pageWidth * 0.085,  // 65/750 = 8.5% (maintains 2:1 aspect ratio)
//       ];

//     case strings.text:
//     case strings.name:
//     case strings.email:
//     case strings.dateSigned:
//     case strings.companyName:
//     case strings.title:
//       return [
//         pageWidth * 0.20,  // DEFAULT_WIDTH_PX/750 ≈ 20%
//         pageWidth * 0.04,  // 32/750 = 4.2%
//       ];

//     case strings.checkbox:
//       return [
//         pageWidth * 0.03,  // 20/750 = 2.6%
//         pageWidth * 0.03,
//       ];

//     default:
//       return [
//         pageWidth * 0.15,
//         pageWidth * 0.04,
//       ];
//   }
// };

export const getNextAvailableId = (requestFile: RequestFile) => {
  const existingIds = requestFile.fields.map((obj: FileField) => obj.id);
  for (let i = 1; i <= existingIds.length + 1; i++) {
    if (!existingIds.includes(i)) {
      return i;
    }
  }
  return existingIds.length + 1;
};

export const getNextRadioGroupId = (requestFile: RequestFile): string => {
  const used = requestFile.fields
    .filter((f: FileField) => f.type === strings.radioButton && f.groupId)
    .map((f: FileField) => f.groupId as string);
  let max = 0;
  used.forEach((gid) => {
    const m = gid.match(/^radio_g_(\d+)$/);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
  });
  return `radio_g_${max + 1}`;
};

export const setIdOnEachField = (
  files: RequestFile[],
  props?: any,
): RequestFile[] => {
  let fieldsIdCounter = 1;
  files?.forEach((file: RequestFile) => {
    if (props && (props.useTemplate || props.updateTemplate)) {
      file.unsignedDoc = file?.unsignedDoc?.substring(
        file?.unsignedDoc?.indexOf("/") + 1,
      );
    }
    file.fields.forEach((field: FileField) => {
      field.id = fieldsIdCounter;
      if (!props) {
        field.existingField = true;
      } else {
        field.existingField = field.existingField!;
      }
      field.maxLength = field.maxLength || 200;
      field.required = field.required || false;
      field.hidden = field.hidden || false;
      field.readOnly = field.readOnly || false;
      field.tabLabel = field.tabLabel || "";
      field.toolTip = field.toolTip || "";
      field.validationPattern = field.validationPattern || "none";
      field.validationMessage = field.validationMessage || "";
      field.placeholder = field.placeholder || "";
      field.multiLine = field.multiLine || false;
      field.styles = field.styles || {
        fontFamily: "Times",
        color: "#1A1A1A",
        size: 16,
        fontWeight: {
          bold: false,
          italic: false,
          underline: false,
        },
      };
      fieldsIdCounter += 1;
    });
  });
  return files;
};

export const addSigner = (
  signer: Partial<RequestParticipant>,
  signers: RequestParticipant[],
  dispatch: Function,
  forBulkSend?: boolean,
) => {
  let documentSigner: Partial<RequestParticipant> = {
    name: signer.name,
    email: signer.email,
    // Carried through so the add-menu can create a copy-only recipient directly.
    cc: signer.cc ?? false,
  };

  let uniqueOrder = 1;
  let uniqueId = 0;

  let nextOrderInSequence =
    signers.length > 0
      ? Math.max(...signers.map((s) => Number(s.order))) + 1
      : 1;

  if (!forBulkSend) {
    const signerOrders: Set<number> = new Set<number>(
      signers.map((signer: any) => parseInt(signer.order)),
    );
    while (signerOrders.has(uniqueOrder)) {
      uniqueOrder++;
    }
  }
  const signerIds: Set<number> = new Set<number>(
    signers.map((signer: any) => signer.id),
  );
  while (signerIds.has(uniqueId)) {
    uniqueId++;
  }

  if (uniqueId <= 19) {
    documentSigner.order =
      nextOrderInSequence <= 20 ? nextOrderInSequence : uniqueOrder;
    documentSigner.id = uniqueId;
    // A color saved before the palette was darkened is still light: normalize
    // it so it holds up as text, initials fill and outline (WCAG 1.4.3).
    documentSigner.color = signer.color
      ? darkenToContrast(signer.color)
      : forBulkSend
        ? darkenToContrast("#A2C579")
        : fieldColor[uniqueId];

    dispatch(
      setReduxSignRequest({
        signers: [...signers, documentSigner],
      }),
    );
  } else {
    openErrorNotification(
      "Oops! You've reached the 20-recipient maximum limit.",
    );
  }
  return documentSigner;
};

export const addApprovers = (
  reviewer: Partial<Reviewers>,
  reviewers: Reviewers[],
  dispatch: Function,
  group?: boolean,
  groupName?: string,
) => {
  let documentReviewer: Partial<Reviewers> = {
    name: reviewer.name,
    email: reviewer.email,
  };
  for (const r of reviewers) {
    if (!r.name || !r.email) {
      openErrorNotification("Please select approver.");
      return null;
    }
    if (r.hasOwnProperty("group") && group && !r.group) {
      openErrorNotification("Please enter group name.");
      return null;
    }
  }
  const reviewerOrderNums: number[] = reviewers
    .map((r: any) => {
      const n = Number(r.order);
      return Number.isFinite(n) ? n : null;
    })
    .filter((n) => n !== null) as number[];

  const reviewerIdNums: number[] = reviewers
    .map((r: any) => {
      const n = Number(r.id);
      return Number.isFinite(n) ? n : null;
    })
    .filter((n) => n !== null) as number[];
  const uniqueOrder =
    reviewerOrderNums.length > 0 ? Math.max(...reviewerOrderNums) + 1 : 1; // orders start at 1
  const uniqueId =
    reviewerIdNums.length > 0 ? Math.max(...reviewerIdNums) + 1 : 0; // ids start at 0

  if (uniqueId <= 19 && uniqueOrder <= 20) {
    documentReviewer.order = uniqueOrder;
    documentReviewer.id = uniqueId;
    documentReviewer.color = reviewer.color
      ? reviewer.color
      : fieldColor[uniqueId];
    if (group) {
      documentReviewer.group = groupName ? groupName : "";
    }
    if (group) {
      const existingGroup = reviewers.find(
        (r) => r.group === groupName && r.groupId,
      );
      documentReviewer.groupId = existingGroup
        ? existingGroup.groupId
        : nanoid();
    }
    let newReviewers = [...reviewers, documentReviewer];
    if (group && groupName) {
      const updatedReviewersList = newReviewers;
      let filteredReviewers = updatedReviewersList.filter(
        (re) => re.group === groupName,
      );

      const groupId = filteredReviewers[0]?.groupId || nanoid();

      let updatedFilteredReviewers = filteredReviewers.map((re) => ({
        ...re,
        order: filteredReviewers[0].order,
        groupId: groupId,
      }));

      newReviewers = updatedReviewersList.map((re) => {
        let updatedReviewer = updatedFilteredReviewers.find(
          (ure) => ure.id === re.id,
        );
        return updatedReviewer ? updatedReviewer : re;
      });
    }
    dispatch(
      setReduxPdfApprovals({
        reviewers: newReviewers,
      }),
    );
  } else {
    openErrorNotification(
      "Oops! You've reached the 20-recipient maximum limit.",
    );
  }
  return documentReviewer;
};

export const getFieldText = (fieldName: string) => {
  switch (fieldName) {
    case strings.signature:
      return { text: "Signature", icon: SignatureFieldIcon };
    case strings.text:
      return { text: "Text", icon: TextFieldIcon };
    case strings.name:
      return { text: "Name", icon: NameFieldIcon };
    case strings.email:
      return { text: "Email", icon: EmailFieldIcon };
    case strings.initials:
      return { text: "Initials", icon: InitialsFieldIcon };
    case "company name":
      return { text: "Company Name", icon: CompanyNameFieldIcon };
    case strings.title:
      return { text: "Title", icon: TitleFieldIcon };
    case "date signed":
      return { text: "Date", icon: DateFieldIcon };
    case strings.radioButton:
      return { text: "Radio button", icon: RadioButtonIcon };
    case strings.date:
      return { text: "Date", icon: DateFieldIcon };
    case strings.dropDown:
      return { text: "Drop down", icon: TextFieldIcon };
    case "checkbox":
      return { text: "Checkbox", icon: CheckboxIcon };
    case strings.attachment:
      return { text: "Attachment", icon: AttachmentFieldIcon };
  }
};

export const DragPreview = ({
  type,
  coords,
  isWithinBounds,
  isMobile,
}: {
  type: string;
  coords: { x: number; y: number };
  isWithinBounds: boolean;
  isMobile?: boolean;
}) => {
  const customFields: CustomField[] = useAppSelector(
    (state) => state.app.customFields ?? [],
  );
  const fieldTextIcon = getFieldText(paletteTypeOf(type, customFields));

  return (
    <Box
      sx={{
        position: "fixed",
        top: coords.y,
        left: coords.x,
        width: isMobile ? 24 : 36,
        height: isMobile ? 24 : 36,
        backgroundColor: "white",
        pointerEvents: "none",
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        border: isWithinBounds
          ? "2px solid " + "#4E4EFF"
          : "2px solid  #E0E0E0",
        borderRadius: "5px",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        transition: "transform 0.2s ease-in-out",
        transform: isWithinBounds ? "rotate(0deg)" : "rotate(10deg)",
      }}
    >
      <img
        src={fieldTextIcon?.icon}
        alt={`${fieldTextIcon?.text || "Field"} icon`}
        width={16}
        aria-hidden="true"
      />
    </Box>
  );
};

export const participantsDropDown = (
  requestParticipants: RequestParticipant[],
  selectedParticipant: RequestParticipant,
  dispatch: any,
  anchorEl: null | HTMLElement,
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void,
  handleClose: () => void,
  isBulkSend: boolean,
  isMobile?: boolean,
  /**
   * Editor left-rail variant from `Sign Request Editor.dc.html`: a 26px solid
   * avatar with the recipient's role on a second line. The default variant is
   * kept for the mobile flow.
   */
  isEditorRail?: boolean,
) => {
  const initials = (selectedParticipant.name ||
    selectedParticipant.requestRole ||
    "A")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  const displayName = isBulkSend
    ? "Signer"
    : selectedParticipant.name ||
    selectedParticipant.requestRole ||
    "Select a signer";

  const roleLine = selectedParticipant.order
    ? `Signer · Level ${selectedParticipant.order}`
    : "Signer";

  return (
    <Box>
      {/* Selected participant */}
      {isEditorRail ? (
        <Box
          role={isBulkSend ? undefined : "button"}
          tabIndex={isBulkSend ? undefined : 0}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          // No aria-label: the visible participant name and role are the
          // button's name (WCAG 2.5.3); aria-haspopup says it opens a menu.
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            !isBulkSend && handleOpen(event);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
            if (!isBulkSend && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              handleOpen(event as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
          sx={{
            width: "100%",
            "&:focus-visible": {
              outline: `2px solid ${focusRing}`,
              outlineOffset: "2px",
            },
            // The app never renders <CssBaseline />, so there is no global
            // `border-box` — without this the padding and border push the
            // button 24px past the rail and it clips at the edge.
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: editorTokens.leftRail.signerGap,
            cursor: isBulkSend ? "default" : "pointer",
            border: `1px solid ${editorTokens.leftRail.signerBorder}`,
            borderRadius: editorTokens.leftRail.signerRadius,
            backgroundColor: "background.paper",
            p: editorTokens.leftRail.signerPadding,
            textAlign: "left",
            "&:hover": {
              backgroundColor: isBulkSend
                ? "background.paper"
                : editorTokens.palette.surface,
            },
          }}
        >
          <Box
            sx={{
              width: editorTokens.leftRail.signerAvatarSize,
              height: editorTokens.leftRail.signerAvatarSize,
              flex: "none",
              borderRadius: "50%",
              backgroundColor:
                selectedParticipant.color || editorTokens.palette.accent,
              color: editorTokens.palette.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: editorTokens.leftRail.signerAvatarFontSize,
              fontWeight: editorTokens.leftRail.signerAvatarFontWeight,
            }}
          >
            {initials}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                display: "block",
                fontSize: editorTokens.leftRail.signerNameFontSize,
                fontWeight: editorTokens.leftRail.signerNameFontWeight,
                color: editorTokens.palette.ink,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "normal",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                display: "block",
                fontSize: editorTokens.leftRail.signerRoleFontSize,
                color: editorTokens.palette.muted,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "normal",
              }}
            >
              {roleLine}
            </Typography>
          </Box>
          <Box
            component="span"
            sx={{ display: "flex", color: editorTokens.palette.body }}
          >
            <ChevronDownIcon size={editorTokens.leftRail.signerCaretSize} />
          </Box>
        </Box>
      ) : (
        <Box
          role={isBulkSend ? undefined : "button"}
          tabIndex={isBulkSend ? undefined : 0}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          aria-label="Select participant"
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: isBulkSend ? "default" : "pointer",
            border: "1px solid",
            borderColor: "signer.border.main",
            borderRadius: "10px",
            backgroundColor: "background.paper",
            px: isMobile ? 1 : 1.25,
            py: isMobile ? 0.75 : 1,
            mb: isMobile ? 1 : 3,
            "&:hover": {
              borderColor: isBulkSend
                ? "signer.border.main"
                : "signer.border.strong",
            },
            "&:focus-visible": {
              outline: `2px solid ${focusRing}`,
              outlineOffset: "2px",
            },
          }}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            !isBulkSend && handleOpen(event);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
            if (!isBulkSend && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              handleOpen(event as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
        >
          <Avatar
            sx={{
              bgcolor: `${selectedParticipant.color}17`,
              color: selectedParticipant.color,
              fontWeight: 600,
              fontSize: "0.75rem",
              width: 32,
              height: 32,
              mr: isMobile ? 1 : 1.5,
            }}
          >
            {initials}
          </Avatar>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: selectedParticipant.color,
              mr: isMobile ? 0.5 : 1.5,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              color: "signer.text.meta",
              fontSize: "1.25rem",
              flexShrink: 0,
            }}
          />
        </Box>
      )}

      {/* Dropdown menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: "rgba(0,0,0,0.11)",
            minWidth: 240,
            border: "1px solid #E1E1E1",
            mt: 0.5,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {requestParticipants.map(
          (requestParticipant: RequestParticipant, idx: number) => {
            const { bg, color } = getColor(requestParticipant.name || "A");
            return (
              <MenuItem
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setReduxSelectedParticipant(requestParticipant));
                  handleClose();
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: `${requestParticipant?.color}17`,
                    color: `${requestParticipant?.color}`,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    width: 32,
                    height: 32,
                  }}
                >
                  {requestParticipant.name
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((word) => word[0])
                    .join("")
                    .toUpperCase()}
                </Avatar>
                <Stack
                  direction="column"
                  sx={{
                    ...(isMobile && {
                      overflowX: "auto",
                      maxWidth: "180px",
                      pb: 1,
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                    }),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: 500,
                      ...(isMobile && { whiteSpace: "nowrap" }),
                    }}
                  >
                    {requestParticipant.name
                      ? requestParticipant.name
                      : "Signer " + (idx + 1)}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 400,
                      ...(isMobile && { whiteSpace: "nowrap" }),
                    }}
                  >
                    {requestParticipant.email}
                  </Typography>
                </Stack>
              </MenuItem>
            );
          },
        )}
      </Menu>
    </Box>
  );
};

export const getContactSuggestion = async (
  searchText: string,
  dispatch: Function,
  excludeIds?: string[],
  setContactList?: Function,
  index?: number,
  key?: string,
) => {
  if (
    hasAccessTo(strings.resources.contacts, strings.fetchPermission)
    // appAdminUserSpecificPermission() ||
    // doesLoggedInUserHasAccess(strings.sendPermission, strings.sign)
  ) {
    try {
      const res = await getContactexclude(
        isTruthy(searchText) ? searchText : " ",
        excludeIds ? excludeIds : [""],
      );
      const contacts = res.map((contact: any) => ({
        name: `${contact.firstName} ${contact.lastName}`,
        email: sanitizeSignerEmail(contact.emailId) || contact.emailId,
        phone: contact.contactNo || "",
      }));
      dispatch(setContacts(contacts));
      if (setContactList) {
        setContactList((prevContactList: any) => ({
          ...prevContactList,
          [key!]: {
            ...prevContactList[key!],
            [index!]: contacts,
          },
        }));
      }
    } catch (error: any) {
      handleErrorMessage(error);
    }
  }
};

export const getField = (
  selectedFile: RequestFile,
  field: FileField,
  coords: Coordinates,
  dispatch: any,
  isMobile?: boolean,
) => {
  switch (field.type) {
    case strings.checkbox:
      return (
        <CheckBoxField
          field={field}
          selectedFile={selectedFile}
          width={coords.pageW}
          height={coords.pageW}
          dispatch={dispatch}
          isMobile={isMobile}
        />
      );

    case strings.radioButton:
      return (
        <RadioField
          field={field}
          selectedFile={selectedFile}
          coords={coords}
          dispatch={dispatch}
          isMobile={isMobile}
        />
      );

    case strings.date:
      return (
        <DateField
          field={field}
          dispatch={dispatch}
          coords={coords}
          isMobile={isMobile}
        />
      );

    case strings.radioButton:
      return (
        <RadioField
          field={field}
          selectedFile={selectedFile}
          coords={coords}
          dispatch={dispatch}
          isMobile={isMobile}
        />
      );

    case strings.date:
      return (
        <DateField
          field={field}
          dispatch={dispatch}
          coords={coords}
          isMobile={isMobile}
        />
      );

    case strings.signature:
    case strings.initials:
      return (
        coords.pageW > 7 && (
          <Stack
            direction={"row"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            spacing={0.5}
          >
            {/* <img
              src={field.type === strings.signature ? SignIcon : InitialIcon}
              alt="signature"
              style={{
                width: "0.9rem",
                height: "0.9rem",
              }}
            /> */}
            <Typography fontSize={"0.8rem"} color={"#646464"} fontWeight={500}>
              {capitalizeText(field.type)}
            </Typography>
          </Stack>
        )
      );

    case strings.attachment: {
      // Once something is attached the chip mirrors the signer's own attachment field
      // (CustomAttachmentFieldElement): a paperclip and "N files", in the recipient's colour. Empty,
      // it keeps the plain "Attachment" label it has always shown -- which is every field on a normal
      // request, where the sender places the field but never fills it.
      const attachedCount = decodeAttachmentValue(field.value).length;
      const attachmentColor = fieldColor[field.signerIndex];
      return (
        coords.pageW > 7 && (
          <Stack
            direction={"row"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            spacing={0.5}
            sx={{ minWidth: 0, overflow: "hidden", px: 0.5 }}
          >
            {attachedCount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexShrink: 0,
                  color: attachmentColor,
                }}
              >
                <PaperclipIcon size={signerAttachmentTokens.pill.iconSize} />
              </Box>
            )}
            <Typography
              fontSize={"0.8rem"}
              color={attachedCount > 0 ? attachmentColor : "#646464"}
              fontWeight={
                attachedCount > 0
                  ? signerAttachmentTokens.pill.fontWeight
                  : 500
              }
              noWrap
            >
              {attachedCount > 0
                ? `${attachedCount} ${attachedCount === 1 ? "file" : "files"}`
                : capitalizeText(field.type)}
            </Typography>
          </Stack>
        )
      );
    }
    default:
      return (
        <ReadOnlyFields
          field={field}
          coords={coords}
          inputPadding={isMobile ? 1 : 4}
        />
      );
  }
};

/** Body of the "Enter Template Name" dialog. */
export const TemplateNameDialogBody = ({
  templateName,
  setTemplateName,
  templateNameError,
  setTemplateNameError,
}: {
  templateName: string;
  setTemplateName: Function;
  templateNameError: string;
  setTemplateNameError: Function;
}) => {
  return (
    // The Modal System's body is left-aligned and already inset, so the field
    // no longer needs the centring wrapper the old shell required.
    <Box>
      <CustomInput
        required
        id="templateName"
        placeHolder="Enter Template name"
        type="text"
        name="templateName"
        label="Template Name"
        value={templateName}
        onChange={(e: any) => {
          setTemplateName(e.target.value);
          if (templateNameError) setTemplateNameError("");
        }}
        error={templateNameError}
        helperText={templateNameError}
      />
    </Box>
  );
};

export const CustomDialogComponent = ({
  openDialog,
  handleDialogClose,
  handleTemplate,
  templateName,
  setTemplateName,
  templateNameError,
  setTemplateNameError,
  updateTemplate,
}: {
  openDialog: boolean;
  handleDialogClose: () => void;
  /** Takes no arguments — the old signature carried an unused click event. */
  handleTemplate: () => void;
  templateName: string;
  setTemplateName: Function;
  templateNameError: string;
  setTemplateNameError: Function;
  updateTemplate: boolean;
}) => {
  return (
    <AppModal
      open={openDialog}
      onClose={handleDialogClose}
      title="Enter Template Name"
      size="sm"
      primaryLabel={updateTemplate ? strings.update : strings.save}
      onPrimary={handleTemplate}
      cancelLabel={strings.cancel}
      onCancel={handleDialogClose}
    >
      <TemplateNameDialogBody
        templateName={templateName}
        setTemplateName={setTemplateName}
        templateNameError={templateNameError}
        setTemplateNameError={setTemplateNameError}
      />
    </AppModal>
  );
};

const hasAttachmentFields = (signRequest: SignRequest): boolean =>
  (signRequest?.files ?? []).some((file: RequestFile) =>
    (file?.fields ?? []).some(
      (field: FileField) => field?.type === strings.attachment,
    ),
  );

/**
 * Makes sure the account's attachment policy is in memory before the Fields step is validated,
 * so `validateField` (synchronous) can read the configured `maxFilesPerSession`. Only fetches
 * when the request actually carries attachment fields, and only once — the helper caches it.
 * A failed fetch is swallowed: the validator then skips the check and the backend rejects the
 * send instead, exactly as it did before.
 */
export const ensureAttachmentPolicyForValidation = async (
  signRequest: SignRequest,
): Promise<void> => {
  if (!hasAttachmentFields(signRequest)) return;
  try {
    await loadAccountAttachmentConfig();
  } catch {
    // Handled by the fallback described above.
  }
};

/**
 * The message to show when some signer has more attachment fields than the account allows
 * them to upload, or `""` when every signer is within the limit (or the limit is not known yet).
 * Same rule as `SignerAttachmentService.applyDefaults` on the backend; the wording follows the
 * other step messages here (one plain "Please …" sentence, no names).
 */
export const attachmentFieldLimitError = (signRequest: SignRequest): string => {
  const maxFilesPerSession =
    getLoadedAccountAttachmentConfig()?.maxFilesPerSession;
  if (!maxFilesPerSession) return "";

  const perSigner = new Map<number, number>();
  for (const file of signRequest?.files ?? []) {
    for (const field of file?.fields ?? []) {
      if (field?.type !== strings.attachment) continue;
      perSigner.set(
        field.signerIndex,
        (perSigner.get(field.signerIndex) ?? 0) + 1,
      );
    }
  }

  // `Array.from`: the build targets ES5, where a Map iterator cannot be used in `for…of`.
  const anySignerOverLimit = Array.from(perSigner.values()).some(
    (count) => count > maxFilesPerSession,
  );
  if (!anySignerOverLimit) return "";
  return `Please add at most ${maxFilesPerSession} attachment fields for each signer.`;
};

export const validateField = (
  signRequest: SignRequest,
  errors: SignRequestErrors,
  dispatch: Function,
  props: any,
  currentStepLabel: string,
  isBulkSend: boolean,
  assignExistingFields: any,
  isTemplate: boolean = false,
) => {
  let isValid: boolean = true;
  let newErrors: SignRequestErrors = { ...errors };

  // Step 0: Upload Document validation
  const validateUploadDocument = () => {
    if (!signRequest.files || signRequest.files.length <= 0) {
      isValid = false;
      newErrors.file = "Please upload at least one document!";
    } else {
      newErrors.file = "";
    }
    return isValid;
  };

  // Step 1: Approvers and Signers validation
  const validateApproversAndSigners = () => {
    if (isTemplate) {
      return isValid;
    }
    // Clear errors from prior steps so they don't surface as the firstErrorMessage.
    newErrors.file = "";
    try {
      // Check if signers exist
      if (!signRequest.signers || signRequest.signers.length === 0) {
        isValid = false;
        newErrors.signers = "Please add at least one signer.";
        return isValid;
      } else {
        newErrors.signers = "";
      }

      // Filter out CC signers for validation
      const signerListWithoutCC = signRequest.signers.filter(
        (signer: RequestParticipant) => !signer.cc,
      );

      if (signerListWithoutCC.length === 0) {
        isValid = false;
        newErrors.signers = "Please add at least one signer without CC.";
        return isValid;
      } else {
        newErrors.signers = "";
      }

      // For bulk send, check if any signer has isValid: false
      if (isBulkSend) {
        const invalidSigners = signerListWithoutCC.filter(
          (signer: RequestParticipant) => signer.isValid === false,
        );

        if (invalidSigners.length > 0) {
          isValid = false;
          const invalidSignerNames = invalidSigners
            .map((signer) => {
              if (signer.name && signer.email) {
                return `${signer.name} (${signer.email})`;
              }
              return signer.name || signer.email || "Unknown";
            })
            .join(", ");

          newErrors.signerEmail = `Please fix invalid name or email for the following signer(s): ${invalidSignerNames}`;
          return isValid;
        }
      }

      // Reset signer-related errors at the start
      newErrors.signerName = "";
      newErrors.signerEmail = "";
      newErrors.signerOrder = "";
      newErrors.signerSchedule = "";
      newErrors.duplicateEmails = "";
      newErrors.securityCode = "";
      newErrors.signerPhone = "";
      newErrors.inPersonHostEmail = "";

      // Validate each signer
      const emailMap = new Map<string, RequestParticipant[]>();
      const reviewerMap = new Map<string, RequestParticipant[]>();

      signRequest.signers.forEach((signer: RequestParticipant) => {
        // Security code mandatory when "Security code" is selected (creating
        // request only). Email OTP and Phone authentication are secured access
        // too but need no code — exclude them.
        const sec = signer.requestSecurity;
        if (
          sec?.needSecuredAccess &&
          sec?.needOTPValidation === false &&
          sec?.needPhoneValidation !== true &&
          (!sec.securityCode || String(sec.securityCode).trim() === "")
        ) {
          isValid = false;
          newErrors.securityCode =
            "Please enter security code where Security code verification is selected.";
        }

        // In-person signers may be blank placeholders — the host captures the
        // name/email at hand-off — so name/email are optional here. Anything
        // that IS entered is still format-checked below.
        const isInPersonSigner =
          signer.deliveryMode === strings.deliveryModes.inPerson;

        // SMS Delivery — the signing link's channels. Non-email channels (SMS /
        // WhatsApp) need a phone number; email becomes optional when the link is
        // texted only (unless email OTP is on).
        const channels =
          signer.deliveryChannels && signer.deliveryChannels.length
            ? signer.deliveryChannels
            : [strings.deliveryChannels.email];
        const hasNonEmailChannel = channels.some(
          (c) => c !== strings.deliveryChannels.email,
        );
        const usesEmailOtp = Boolean(
          signer.requestSecurity?.needSecuredAccess &&
          signer.requestSecurity?.needOTPValidation,
        );
        const emailOptional =
          isInPersonSigner ||
          (!channels.includes(strings.deliveryChannels.email) && !usesEmailOtp);

        // Validate name (optional for in-person)
        if (!isInPersonSigner && (!signer.name || signer.name.trim() === "")) {
          isValid = false;
          newErrors.signerName = "Please enter signer name!";
        }

        // Validate name length
        if (signer.name && !isValidParticipantNameLength(signer.name)) {
          isValid = false;
          newErrors.signerName =
            getParticipantNameError(signer.name) || "Name is invalid";
        }

        // Phone is required — and must be a valid international number — for SMS
        // delivery (the backend 400s on an invalid E.164 number).
        if (hasNonEmailChannel) {
          const deliveryPhone = (signer.phone || "").trim();
          if (!deliveryPhone) {
            isValid = false;
            newErrors.signerPhone = strings.smsDelivery.phoneRequired;
          } else if (!isPhoneValid(deliveryPhone)) {
            isValid = false;
            newErrors.signerPhone = strings.smsDelivery.phoneInvalid;
          }
        }

        // Phone (SMS OTP) authentication needs a valid phone number to text the code.
        if (
          signer.requestSecurity?.needSecuredAccess &&
          signer.requestSecurity?.needPhoneValidation
        ) {
          const authPhone = (signer.requestSecurity?.authPhone || "").trim();
          if (!authPhone) {
            isValid = false;
            newErrors.signerPhone = strings.smsDelivery.authPhoneRequired;
          } else if (!isPhoneValid(authPhone)) {
            isValid = false;
            newErrors.signerPhone = strings.smsDelivery.phoneInvalid;
          }
        }

        // Validate email (optional for in-person / SMS-only; format enforced if set)
        if (!signer.email || signer.email.trim() === "") {
          if (!emailOptional) {
            isValid = false;
            newErrors.signerEmail = "Please enter signer email!";
          }
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const emailLengthError = getParticipantEmailError(signer.email);
          if (emailLengthError) {
            // The row already shows this live; the step has to block on it too,
            // or Next would sail past a limit the field says was exceeded.
            isValid = false;
            newErrors.signerEmail = emailLengthError;
          } else if (!emailRegex.test(signer.email)) {
            isValid = false;
            newErrors.signerEmail = "Please enter valid email";
          } else {
            // Check for duplicate emails
            const email = signer.email.toLowerCase();
            if (emailMap.has(email)) {
              emailMap.get(email)?.push(signer);
            } else {
              emailMap.set(email, [signer]);
            }
          }
        }

        // Validate order if required
        if (
          signRequest.signerOrderRequired &&
          (!signer.order || signer.order <= 0)
        ) {
          isValid = false;
          newErrors.signerOrder = "Please enter valid signer order!";
        }

        // Validate schedule time if provided
        if (signer.scheduleTime) {
          const scheduleDate = convertEstToLocalTimezone(
            signer.scheduleTime,
            "MM/DD/YYYY HH:mm:ss",
          );
          if (scheduleDate <= moment()) {
            isValid = false;
            newErrors.signerSchedule =
              "Please enter future date/time for signer delay";
          }
        }
      });

      // Check for duplicate emails
      emailMap.forEach((signers) => {
        if (signers.length > 1) {
          isValid = false;
          const duplicateNames = signers.map((s) => s.name).join(", ");
          newErrors.duplicateEmails = `Signers ${duplicateNames} have the same email`;
        }
      });

      // Host email required once any non-CC signer signs in person — the host
      // is the one handing their device over, so the envelope needs a way to
      // reach them.
      const hasInPersonSigner = signerListWithoutCC.some(
        (signer: RequestParticipant) =>
          signer.deliveryMode === strings.deliveryModes.inPerson,
      );
      if (hasInPersonSigner) {
        const hostEmail = signRequest.signRequestConfig?.inPersonHostEmail;
        if (!hostEmail || hostEmail.trim() === "") {
          isValid = false;
          newErrors.inPersonHostEmail = "Please enter the host's email!";
        } else if (!strings.regex.test(hostEmail)) {
          isValid = false;
          newErrors.inPersonHostEmail = "Please enter a valid host email";
        }
      }

      // Reset approver errors
      newErrors.approvers = "";
      newErrors.approverName = "";
      newErrors.approverEmail = "";

      // Validate approvers if approval is needed
      if (
        isTruthy(signRequest.approval.approvalNeeded) &&
        signRequest.approval?.reviewers?.length > 0
      ) {
        signRequest.approval.reviewers.forEach(
          (reviewer: RequestParticipant) => {
            const revSec = reviewer.requestSecurity;
            if (
              revSec?.needSecuredAccess &&
              revSec?.needOTPValidation === false &&
              (!revSec.securityCode ||
                String(revSec.securityCode).trim() === "")
            ) {
              isValid = false;
              newErrors.securityCode =
                "Please enter security code where Security code verification is selected.";
            }

            if (!reviewer.name || reviewer.name.trim() === "") {
              isValid = false;
              newErrors.approverName = "Please enter approver name!";
            }

            if (!reviewer.email || reviewer.email.trim() === "") {
              isValid = false;
              newErrors.approverEmail = "Please enter approver email!";
            } else {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(reviewer.email)) {
                isValid = false;
                newErrors.approverEmail = "Please enter valid approver email";
              } else {
                const email = reviewer.email.toLowerCase();
                if (reviewerMap.has(email)) {
                  reviewerMap.get(email)?.push(reviewer);
                } else {
                  reviewerMap.set(email, [reviewer]);
                }
              }
            }
          },
        );

        reviewerMap.forEach((reviewer) => {
          if (reviewer.length > 1) {
            isValid = false;
            const duplicateNames = reviewer.map((r) => r.name).join(", ");
            newErrors.duplicateEmails = `Approvers ${duplicateNames} have the same email`;
          }
        });
      }
    } catch (err) {
      console.log("error", err);
    }

    return isValid;
  };

  // Step 2: Sign Request Editor validation
  const validateSignRequestEditor = () => {
    // Reset field-related errors
    newErrors.fields = "";
    newErrors.readOnlyFields = "";
    newErrors.fieldPosition = "";
    newErrors.missingSignatures = "";
    newErrors.fieldMaxLength = "";
    newErrors.invalidEmailFields = "";
    // Clear errors from prior steps so they don't surface as the firstErrorMessage.
    newErrors.file = "";
    newErrors.signers = "";
    newErrors.signerName = "";
    newErrors.signerEmail = "";
    newErrors.signerOrder = "";
    newErrors.signerSchedule = "";
    newErrors.duplicateEmails = "";
    newErrors.securityCode = "";
    newErrors.inPersonHostEmail = "";
    newErrors.approvers = "";
    newErrors.approverName = "";
    newErrors.approverEmail = "";

    if (isBulkSend) {
      const unassignedExistingFieldsFile = signRequest.files.find(
        (file: RequestFile) =>
          assignExistingFields[file.unsignedPdf || file.unsignedDoc],
      );
      if (unassignedExistingFieldsFile) {
        dispatch(setReduxSelectedFile(unassignedExistingFieldsFile));
      } else {
        const hasSignfield = signRequest.files.find((file: RequestFile) =>
          file.fields.find(
            (field: FileField) => field.type === strings.signature,
          ),
        );

        if (!hasSignfield) {
          openErrorNotification("Please add at-least one sign field.");
        }
        return hasSignfield;
      }
    }

    if (!signRequest.files || signRequest.files.length === 0) {
      isValid = false;
      newErrors.fields = "No documents available for field assignment";
      return isValid;
    }

    // Create a map to track signers and their signature fields
    const signerSignaturesMap: any = new Map<number, boolean>();
    signRequest.signers
      .filter((signer) => !signer.cc)
      .forEach((signer) => {
        signerSignaturesMap.set(signer.id, false);
      });

    // Check each field in all files
    for (const file of signRequest.files) {
      if (!file.fields) continue;

      const textLikeFieldTypes = [
        strings.name,
        strings.email,
        strings.dateSigned,
        strings.text,
        strings.title,
        strings.companyName,
      ];

      for (const field of file.fields) {
        // Configuration the server would refuse on send, caught here so the sender sees it in the
        // editor beside the field rather than as a toast on the next step.
        const configError = customFieldConfigError(field);
        if (configError) {
          isValid = false;
          newErrors.customFieldConfig = configError;
        }

        // A drop down with no options is a dead end: the signer is handed a list with nothing to
        // pick. A library drop down is already covered by customFieldConfigError above, so this is
        // the same rule for one dragged from the Fields panel. A drop down imported from the PDF's
        // own form keeps its options inside the document, so it is left alone -- the same exemption
        // the server makes in validateCustomFieldConfiguration.
        if (
          field.type === strings.dropDown &&
          !(field as any).customFieldId &&
          !field.existingField
        ) {
          const options = (field.listItems ?? []).map((item) =>
            (item?.text ?? "").trim(),
          );
          const name = field.label || "the drop down field";
          if (!options.length || options.every((text) => !text)) {
            isValid = false;
            newErrors.fields = `Add at least one option to ${name}, or remove it. A signer cannot pick from an empty list.`;
          } else if (options.some((text) => !text)) {
            isValid = false;
            newErrors.fields = `Give every option of ${name} a label, or remove the blank one.`;
          }
        }

        // Validate required read-only fields. A library field is pre-filled through defaultValue,
        // so that counts as the value here -- otherwise a read-only custom field with a default
        // would be reported as empty.
        const readOnlyValue =
          (field as any).customFieldId && (field.defaultValue ?? "").trim()
            ? field.defaultValue
            : field.value;
        if (
          field.readOnly &&
          field.type !== strings.checkbox &&
          field.type !== strings.radioButton &&
          (!readOnlyValue || String(readOnlyValue).trim() === "")
        ) {
          isValid = false;
          newErrors.readOnlyFields =
            "Please enter values in all read-only fields.";
        }

        // The Properties panel writes Max Length straight into the request, so a value past the
        // ceiling is stored even while its inline error shows. The server only checks this on
        // library fields; stop every other text field here.
        if (
          textLikeFieldTypes.includes(field.type) &&
          ((field.maxLength ?? 0) > MAX_FIELD_LENGTH ||
            (field.minLength ?? 0) > MAX_FIELD_LENGTH)
        ) {
          isValid = false;
          newErrors.fieldMaxLength = `The length limits of ${
            field.label || "a text field"
          } cannot exceed ${MAX_FIELD_LENGTH}.`;
        }

        // Validate that any pre-filled value fits within the field's maxLength.
        if (textLikeFieldTypes.includes(field.type) && field.value) {
          const cap = field.maxLength || 200;
          if (field.value.length > cap) {
            isValid = false;
            newErrors.fieldMaxLength = `Field value exceeds max length (${cap}).`;
          }
        }

        if (
          field.type === strings.email &&
          field.value &&
          field.value.trim() !== ""
        ) {
          if (!strings.regex.test(field.value.trim())) {
            console.log("email regex", strings.regex);
            isValid = false;
            newErrors.invalidEmailFields =
              "Please enter a valid email address in Email fields.";
          }
        }

        // Track signature fields for each signer
        if (
          field.type === "signature" &&
          signerSignaturesMap.has(field.signerIndex)
        ) {
          signerSignaturesMap.set(field.signerIndex, true);
        }

        // Validate field positioning with page-percentage coordinates.
        // Using fieldPage* avoids stale x/y values after drag/resize updates.
        const fieldX = Number(field.fieldPageX ?? field.x ?? 0);
        const fieldY = Number(field.fieldPageY ?? field.y ?? 0);
        const fieldW = Number(field.fieldPageWidth ?? field.w ?? 0);
        const fieldH = Number(field.fieldPageHeight ?? field.h ?? 0);

        const outOfBounds =
          fieldX < 0 ||
          fieldY < 0 ||
          fieldX + fieldW > 100 ||
          fieldY + fieldH > 100;

        if (outOfBounds) {
          isValid = false;
          newErrors.fieldPosition =
            "Fields must be positioned within document bounds";
        }
      }
    }

    // Ensure every signer has at least one signature field
    for (const [signerId, hasSignature] of signerSignaturesMap.entries()) {
      if (!hasSignature) {
        isValid = false;
        newErrors.missingSignatures =
          "Please add at least one signature field for each signer";
        break;
      }
    }

    // No signer may be asked for more attachments than the account lets them upload
    // (Settings → Document Settings → max files per session), or the envelope can never be
    // completed. Mirrors SignerAttachmentService.applyDefaults on the backend, which stays as
    // the safety net; this just surfaces it here instead of on the final Send.
    const attachmentLimitError = attachmentFieldLimitError(signRequest);
    if (attachmentLimitError) {
      isValid = false;
      newErrors.fields = attachmentLimitError;
    }

    return isValid;
  };

  // Step 3: Email configurations validation
  const validateEmailConfigurations = () => {
    // Reset email configuration errors
    newErrors.expirationDays = "";
    newErrors.daysBeforeFirstReminder = "";
    newErrors.daysBetweenReminders = "";
    newErrors.schedule = "";
    newErrors.emailSubject = "";
    newErrors.emailMessage = "";
    // Clear errors from prior steps so they don't surface as the firstErrorMessage.
    newErrors.file = "";
    newErrors.signers = "";
    newErrors.signerName = "";
    newErrors.signerEmail = "";
    newErrors.signerOrder = "";
    newErrors.signerSchedule = "";
    newErrors.duplicateEmails = "";
    newErrors.securityCode = "";
    newErrors.inPersonHostEmail = "";
    newErrors.approvers = "";
    newErrors.approverName = "";
    newErrors.approverEmail = "";
    newErrors.fields = "";
    newErrors.readOnlyFields = "";
    newErrors.fieldPosition = "";
    newErrors.missingSignatures = "";
    newErrors.fieldMaxLength = "";
    newErrors.invalidEmailField = "";

    // Validate expiration days
    if (!signRequest.expirationDays || signRequest.expirationDays < 1) {
      isValid = false;
      newErrors.expirationDays = "Expiration days must be greater than zero!";
    } else if (signRequest.expirationDays > 180) {
      isValid = false;
      newErrors.expirationDays = "Expiration days must be less than 180!";
    }

    // Validate reminders if enabled
    if (signRequest.needReminders) {
      if (
        !signRequest.reminders?.daysBeforeFirstReminder ||
        signRequest.reminders.daysBeforeFirstReminder < 1
      ) {
        isValid = false;
        newErrors.daysBeforeFirstReminder =
          "Days of the first reminder must be greater than zero!";
      }

      if (
        !signRequest.reminders?.daysBetweenReminders ||
        signRequest.reminders.daysBetweenReminders < 1
      ) {
        isValid = false;
        newErrors.daysBetweenReminders =
          "Days of the reminder repetition must be greater than zero!";
      }
    }

    // Validate scheduled time if provided
    if (signRequest.scheduleTime) {
      const scheduleDate = convertEstToLocalTimezone(
        signRequest.scheduleTime,
        "MM/DD/YYYY HH:mm:ss",
      );

      if (scheduleDate <= moment()) {
        isValid = false;
        newErrors.schedule = "Please enter future date/time";
      }
    }

    // Validate email subject
    if (!signRequest.emailSubject || signRequest.emailSubject.trim() === "") {
      isValid = false;
      newErrors.emailSubject = "Please enter email subject.";
    } else if (signRequest.emailSubject.length > 100) {
      isValid = false;
      newErrors.emailSubject = "Email subject must not exceed 100 characters.";
    }

    // Validate email message length
    if (signRequest.emailMessage && signRequest.emailMessage.length > 1000) {
      isValid = false;
      newErrors.emailMessage = "Email message must not exceed 1000 characters.";
    }

    return isValid;
  };

  // Run validation based on current step
  switch (currentStepLabel) {
    case "Upload Document":
      validateUploadDocument();
      break;
    case "Approvers and Signers":
    case "Signers":
      validateApproversAndSigners();
      break;
    case "Sign Request Editor":
      validateSignRequestEditor();
      break;
    case "Email configurations":
      validateEmailConfigurations();
      break;
    default:
      break;
  }

  // Dispatch errors so UI can show them; return first message so caller can show notification immediately (avoids stale state on first click)
  const firstErrorMessage =
    (Object.values(newErrors).find(Boolean) as string | undefined) || undefined;

  if (props.newTemplate || props.updateTemplate) {
    dispatch(setErrors(newErrors));
    return { isValid, firstErrorMessage };
  }

  dispatch(setErrors(newErrors));
  return { isValid, firstErrorMessage };
};

export const handleDraftBtn = async (
  signRequest: SignRequest,
  setSaveAsDraft: Function,
  dispatch: Function,
  skipRedirect?: boolean,
) => {
  try {
    dispatch(setLoading(true));
    const reqObj = setCoordinates(signRequest);
    if (!isTruthy(reqObj.id)) {
      const res = await getEnvelopeId();
      reqObj.id = res.message;
    }
    const updatedReqObj = {
      ...reqObj,
      signers: reqObj.signers.map(({ status, ...rest }) => rest),
      status: "",
    };
    await saveDraftSignRequest(updatedReqObj);
    dispatch(setReduxSignRequest(undefined));
    setSaveAsDraft(false);
    dispatch(setGoBackPopup(false));
    openSuccessNotification("saved your changes as a draft.");
    if (!skipRedirect) {
      history.push(viewpaths.sentSignRequestsViewPath);
    }
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    dispatch(setLoading(false));
  }
};

// Valid field types (normalized with underscores)
const VALID_FIELD_TYPES = [
  "signature",
  "name",
  "email",
  "initials",
  "text",
  "date_signed",
  "checkbox",
  "radio_button",
  "button",
  "drop_down",
  "company_name",
  "title",
  "attachment",
];

// Normalize field type: extract type from formats like "Yvette-date_signed-1", "Yvette-signature-1", etc.
const validateAndNormalizeFieldType = (value: string): string => {
  if (!value || typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();

  // Check if value matches pattern: "signer-fieldtype-count"
  // Examples: "Yvette-date_signed-1", "Yvette-signature-1", "Yvette-company_name-1", "Yvette-text-1"
  const patternMatch = trimmedValue.match(
    /^[^-]+-([a-z_]+(?:[_-][a-z_]+)*)-?\d*$/i,
  );
  if (patternMatch) {
    const extractedType = patternMatch[1].toLowerCase();
    const normalizedType = extractedType
      .replace(/\s+/g, "_")
      .replace(/-/g, "_");

    if (VALID_FIELD_TYPES.includes(normalizedType)) {
      return normalizedType;
    }

    // Also check if it matches any valid type when we try different separators
    for (const validType of VALID_FIELD_TYPES) {
      if (
        normalizedType === validType ||
        normalizedType === validType.replace(/_/g, "-") ||
        normalizedType === validType.replace(/_/g, " ")
      ) {
        return validType;
      }
    }
  }

  // If value is already a valid type (with or without underscores/spaces), normalize it
  const normalizedValue = trimmedValue
    .replace(/\s+/g, "_")
    .replace(/-/g, "_")
    .toLowerCase();
  if (VALID_FIELD_TYPES.includes(normalizedValue)) {
    return normalizedValue;
  }

  // If value contains a valid type as substring, try to extract it
  for (const validType of VALID_FIELD_TYPES) {
    const typeWithSpaces = validType.replace(/_/g, " ");
    const typeWithDashes = validType.replace(/_/g, "-");
    if (
      trimmedValue.toLowerCase().includes(validType) ||
      trimmedValue.toLowerCase().includes(typeWithSpaces) ||
      trimmedValue.toLowerCase().includes(typeWithDashes)
    ) {
      return validType;
    }
  }

  return trimmedValue;
};

// Validate and normalize all field types in a SignRequest
export const validateAndNormalizeSignRequestFieldTypes = (
  signRequest: SignRequest,
): { isValid: boolean; errors: string[]; normalizedRequest: SignRequest } => {
  const errors: string[] = [];
  const normalizedRequest = { ...signRequest };

  if (!normalizedRequest.files || normalizedRequest.files.length === 0) {
    return { isValid: true, errors: [], normalizedRequest };
  }

  normalizedRequest.files = normalizedRequest.files.map((file: RequestFile) => {
    if (!file.fields || file.fields.length === 0) {
      return file;
    }

    const normalizedFields = file.fields.map((field: FileField) => {
      if (!field.type) {
        return field;
      }

      // Always try to normalize the field type
      const normalizedType = validateAndNormalizeFieldType(field.type);

      // Check if normalization produced a valid type
      if (VALID_FIELD_TYPES.includes(normalizedType)) {
        // Return field with normalized type (even if it was different from original)
        return {
          ...field,
          type: normalizedType,
        };
      }

      // If normalization failed, report error but still try to use the normalized value
      const fileName = file.unsignedPdf || file.unsignedDoc || "Unknown";
      errors.push(
        `Invalid field type "${field.type}" in field ID ${field.id} of file "${fileName}". Could not normalize to a valid type. Valid types are: ${VALID_FIELD_TYPES.join(", ")}`,
      );

      // Still return the normalized type (even if invalid) so it can be corrected
      return {
        ...field,
        type: normalizedType,
      };
    });

    return {
      ...file,
      fields: normalizedFields,
    };
  });

  return {
    isValid: errors.length === 0,
    errors,
    normalizedRequest,
  };
};

export const sendSignRequest = async (
  pdfSignRequest: SignRequest,
  isRequest: boolean,
  dispatch: Function,
  props: any,
  setConfirmNavigation?: Function,
  editRequest?: boolean,
) => {
  try {
    dispatch(setLoading(true));

    // Validate and normalize field types before processing
    // This will automatically fix types like "Yvette-date_signed-1" to "date_signed"
    const validationResult =
      validateAndNormalizeSignRequestFieldTypes(pdfSignRequest);

    // Always use the normalized request (with corrected field types)
    const reqObj = setCoordinates(validationResult.normalizedRequest);
    if (!reqObj.needReminders) reqObj.reminders = null;
    if (isRequest) {
      if (!editRequest) {
        await sendPdfSignRequest(reqObj, isRequest);
      } else if (
        reqObj.status === strings.pending ||
        reqObj.status === strings.inReview
      ) {
        await updateSignRequest(reqObj);
        openSuccessNotification("Request updated successfully!");
        dispatch(setReduxSignRequest(undefined));
        dispatch(setSelectedFieldDrawer(false));
        dispatch(setSelectedField(null));
        history.push(viewpaths.dashboardViewPath);
      }
    } else {
      if (props.newTemplate) {
        await saveTemplate({ ...reqObj, type: "template" }, isRequest);
        openSuccessNotification("Template is saved!");
        // history.push(viewpaths.templateFolders);
        dispatch(setReduxSignRequest(undefined));
      } else if (props.updateTemplate) {
        await updateTemplate(reqObj, isRequest);
        openSuccessNotification("Template is Updated!");
        // history.push(viewpaths.templateFolders);
        dispatch(setReduxSignRequest(undefined));
        // Auto-close the tab if opened from the Web Form Builder flow
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("fromWebForm") === "true") {
          const channel = new BroadcastChannel("template_updates");
          channel.postMessage({
            type: "TEMPLATE_UPDATED",
            templateId: reqObj.id,
          });
          channel.close();

          localStorage.setItem(
            `webform_template_${reqObj.id}`,
            JSON.stringify(reqObj),
          );

          window.close();
          return;
        }
      } else if (props.useTemplate || props.editDraft) {
        await sendPdfSignRequest(reqObj, isRequest);
      }
    }
  } catch (error: any) {
    handleErrorMessage(error);
    throw error;
  } finally {
    dispatch(setReduxSignRequest(undefined));
    dispatch(setLoading(true));
  }
};

export const getDocumentDimensions = async (
  selectedFile: RequestFile,
  isRequest: boolean,
  signRequest: SignRequest,
  dispatch: any,
) => {
  let dimensions;
  try {
    dimensions = await fetchDocumentDimensions(
      { unsignedPdf: selectedFile.unsignedDoc },
      isRequest,
      signRequest.id,
      false,
    );
  } catch (error) {
    // handleErrorMessage(error);
  }

  // Only persist dimensions when the fetch actually returned them. Writing an
  // undefined/empty result back would set the file's `dimensions` to `{}` (via
  // setReduxSelectedFile's spread) — a truthy-but-useless value that blocks any
  // retry, so the document's existing fields never get real coordinates and
  // can't be moved. Leaving it untouched lets the loader retry once the envelope
  // id is available.
  if (!isTruthy(dimensions)) return;

  const updatedFile = {
    ...selectedFile,
    dimensions,
  };
  dispatch(setReduxSelectedFile(updatedFile));
  dispatch(updateFileInSignRequest(updatedFile));
};

export const getStatusMessage = (
  requestStatus: string,
  currentReviewer: RequestParticipant,
  reviewers: RequestParticipant[],
): string => {
  // Show approve/reject buttons if reviewer is pending and request is in review
  if (
    currentReviewer.status === strings.pending &&
    requestStatus === strings.inReview
  ) {
    return ""; // handled separately (buttons)
  }

  // Handle global request statuses
  if (requestStatus === strings.rejected) {
    const rejectedReviewer = reviewers.find(
      (reviewer) => reviewer.status === strings.rejected,
    );
    return `Rejected by ${rejectedReviewer?.name} (${rejectedReviewer?.email})`;
  }

  if (requestStatus === strings.voided) {
    return "This request is voided";
  }

  // Handle current reviewer-specific statuses
  switch (currentReviewer.status) {
    case strings.approved:
      return "Approved by you.";
    case strings.rejected:
      return "Rejected by you.";
    case strings.skipped: {
      const approvedReviewer = reviewers.find(
        (reviewer) =>
          reviewer.group === currentReviewer.group &&
          reviewer.status === strings.approved,
      );
      return approvedReviewer
        ? `Approved by ${approvedReviewer.name} (${approvedReviewer.email}).`
        : "Action already taken.";
    }
    default:
      return "Action already taken.";
  }
};

export const generatePayload = (
  selectedFiles: RequestFile[],
  question: string,
  requestId: SignRequest,
) => {
  const filesPayload = selectedFiles.map((file) => ({
    fileName: file.unsignedDoc,
    requestId: requestId.id,
  }));
  return {
    files: filesPayload,
    question: question,
  };
};
